import { Box, NewTabIcon } from '@common-fe/common-fe';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

/** Rim tones from the border-sweep dev handoff (EL-32635). */
const SWEEP_BASE_COLOR = '#f6c6c6';
const SWEEP_HIGHLIGHT_COLOR = '#e05252';

/**
 * Registers the animatable sweep angle globally.
 *
 * A CSS `@property` rule only registers when it lives at the top level of a stylesheet. Declared
 * inside a styled-components rule it is silently dropped, so `conic-gradient(from var(--angle))`
 * never interpolates and the border looks static. Rendering this global style once (while a card
 * animates) registers the property so the angle animates smoothly.
 */
export const SweepAngleGlobalStyle = createGlobalStyle`
  @property --sweep-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
`;

/** One full clockwise lap of the highlight around the border, 4000ms linear. */
const borderSweep = keyframes`
  to {
    --sweep-angle: 360deg;
  }
`;

/**
 * Shared "new-tab" icon for statistic cards that link to a details report.
 * It is defined here so {@link StatisticCard} can target it in its hover rule.
 */
export const StatisticDetailsIcon = styled(NewTabIcon)``;

interface StatisticCardProps {
  /**
   * When true, paints a one-time animated red border sweep around the card
   * (used by the Fraud Review Required metric — EL-32635).
   *
   * The rim is drawn by a masked `::before` overlay: a `conic-gradient(from var(--sweep-angle))`
   * masked to a 2px ring so it only paints the border edge, layered on top of the card's own
   * (untouched) background and border. Animating `--sweep-angle` rotates the highlight around the
   * rim. It is skipped when the user prefers reduced motion.
   *
   * Requires {@link SweepAngleGlobalStyle} to be mounted so the swept angle interpolates.
   */
  $isAnimating?: boolean;
}

/**
 * Shared wrapper for every metric card in the New Statistics section.
 *
 * The design asks for a 1px `border2` outline that becomes a 2px `border1` one on hover.
 * The extra pixel is painted with a matching outline so the card never shifts on hover, and
 * any nested {@link StatisticDetailsIcon} switches to the accent color on hover.
 */
export const StatisticCard = styled(Box)<StatisticCardProps>`
  transition: all 0.32s;
  outline: 1px solid transparent;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border1};
    outline: 1px solid ${({ theme }) => theme.colors.border1};

    ${StatisticDetailsIcon} {
      fill: ${({ theme }) => theme.colors.iconAccent};
    }
  }
  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      /*
       * The card keeps its own opaque background and 1px border untouched, so it can never flash
       * transparent and never changes size. The animated rim is drawn by an absolutely-positioned
       * "::before" overlay masked to a ring, sitting on top of the border.
       */
      position: relative;

      &::before {
        content: '';
        position: absolute;
        /* Cover the border box exactly; -1px lines the ring up with the card's own 1px border. */
        inset: -1px;
        border-radius: inherit;
        pointer-events: none;
        z-index: 1;
        /* 2px-thick ring: the conic gradient shows only in the padding band. */
        padding: 2px;
        background: conic-gradient(
          from var(--sweep-angle),
          ${SWEEP_BASE_COLOR} 0deg,
          ${SWEEP_HIGHLIGHT_COLOR} 40deg,
          ${SWEEP_BASE_COLOR} 90deg,
          ${SWEEP_BASE_COLOR} 360deg
        );
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        mask-composite: exclude;
        animation: ${borderSweep} 4000ms linear forwards;
      }

      @media (prefers-reduced-motion: reduce) {
        &::before {
          animation: none;
        }
      }
    `}
`;
